import { computed, toValue, useSlots, watch, type MaybeRefOrGetter } from 'vue'

/**
 * Whether a table should hand the screen over to its `empty` slot — the panel that
 * explains a feature the publisher has not started using yet.
 *
 * It is deliberately hard to satisfy, because that panel claims "you have nothing", which
 * is a lie in every other state that also renders no rows. It stays away while the query
 * is unsettled, loading, or refused, and while a search or a filter is narrowing the list
 * — an empty *result* is `Table`'s own "no data", not this. So a widget can leave the
 * panel mounted and let this decide.
 *
 * It calls `useSlots` itself, which means it may only be called from `setup`, like
 * any composable.
 */
export function useTableEmptySlot(options: {
  table: {
    isLoading: MaybeRefOrGetter<boolean>
    total: MaybeRefOrGetter<number>
    isFetched?: MaybeRefOrGetter<boolean>
    /** The list was refused — `Table` says so itself, and this panel stays away. */
    isError?: MaybeRefOrGetter<boolean>
  }
  /**
   * Whether the caller was given an `empty` slot to show. Defaults to the calling
   * component's own `useSlots().empty`, so leave it out — pass it only when the panel that
   * would show is not that slot.
   */
  hasEmptySlot?: MaybeRefOrGetter<boolean>
  search?: MaybeRefOrGetter<string | null | undefined>
  /**
   * True while the list is narrowed by filters. An empty result then means
   * "nothing matches", not "nothing exists" — so the empty state stays hidden.
   */
  filtered?: MaybeRefOrGetter<boolean>
  /**
   * Notified whenever the decision flips, and once on setup: a page widget hides its
   * toolbar while the empty state shows, so it has to learn the initial value too.
   */
  onChange?: (showEmptySlot: boolean) => void
}) {
  // Legal because the helper itself is only ever called from `setup`, which is the only
  // place `useSlots` may be called.
  const ownSlots = useSlots()

  const hasEmptySlot = computed(() =>
    options.hasEmptySlot === undefined
      ? !!ownSlots.empty
      : toValue(options.hasEmptySlot)
  )

  const showEmptySlot = computed(() => {
    if (!hasEmptySlot.value) return false
    if (
      options.table.isFetched !== undefined &&
      !toValue(options.table.isFetched)
    ) {
      return false
    }
    if (toValue(options.table.isError)) return false
    if (toValue(options.table.isLoading)) return false
    if (toValue(options.table.total) !== 0) return false
    if (toValue(options.filtered)) return false

    const search = (toValue(options.search) ?? '').trim()
    if (search !== '') return false

    return true
  })

  if (options.onChange) {
    watch(showEmptySlot, options.onChange, { immediate: true })
  }

  return showEmptySlot
}
